import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaDownload, FaUserCheck } from "react-icons/fa";
import AvailableDriversCellModal from "../components/AvailableDriversCellModal";
import AvailableDriversService from "../services/AvailableDriversService";

const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIME_OPTIONS = [
  "12 AM",
  "1 AM",
  "2 AM",
  "3 AM",
  "4 AM",
  "5 AM",
  "6 AM",
  "7 AM",
  "8 AM",
  "9 AM",
  "10 AM",
  "11 AM",
  "12 PM",
  "1 PM",
  "2 PM",
  "3 PM",
  "4 PM",
  "5 PM",
  "6 PM",
  "7 PM",
  "8 PM",
  "9 PM",
  "10 PM",
  "11 PM",
];

const STATUS_OPTIONS = [
  { value: "both", label: "Both" },
  { value: "online", label: "Online" },
  { value: "offline", label: "Offline" },
];

const selectClassName =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

const highlightColumnClass = "bg-blue-100 ring-1 ring-inset ring-blue-300";

function getTodayWeekdayIST() {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function createDefaultFilters() {
  return {
    startDay: getTodayWeekdayIST(),
    startTime: "",
    pincode: "",
    status: "both",
  };
}

const createDefaultSummary = () => ({
  totalOnboarded: 0,
  online: 0,
  offline: 0,
});

const AvailableDrivers = () => {
  const [formFilters, setFormFilters] = useState(createDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(createDefaultSummary);
  const [evaluationDay, setEvaluationDay] = useState("");
  const [loading, setLoading] = useState(true);
  const [cellModal, setCellModal] = useState(null);

  const fetchRows = useCallback(async (filters) => {
    setLoading(true);
    try {
      const params = { day: filters.startDay, status: filters.status };
      if (filters.pincode.length === 6) params.pincode = filters.pincode;

      const response = await AvailableDriversService.getAvailableDrivers(params);
      if (response?.success) {
        setRows(response.data?.rows || []);
        setSummary(response.data?.summary || createDefaultSummary());
        setEvaluationDay(response.data?.evaluationDay || "");
      } else {
        setRows([]);
        setSummary(createDefaultSummary());
        setEvaluationDay("");
      }
    } catch {
      setRows([]);
      setSummary(createDefaultSummary());
      setEvaluationDay("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(createDefaultFilters());
  }, [fetchRows]);

  const handleReset = () => {
    const defaults = createDefaultFilters();
    setFormFilters(defaults);
    setAppliedFilters(defaults);
    fetchRows(defaults);
  };

  const handleApplyFilter = () => {
    setAppliedFilters({ ...formFilters });
    fetchRows(formFilters);
  };

  const handleFilterChange = (field, value) => {
    if (field === "pincode") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      setFormFilters((prev) => ({ ...prev, pincode: digitsOnly }));
      return;
    }
    setFormFilters((prev) => ({ ...prev, [field]: value }));
  };

  const highlightedHourIndex = useMemo(() => {
    if (!appliedFilters.startTime) return -1;
    return TIME_OPTIONS.indexOf(appliedFilters.startTime);
  }, [appliedFilters.startTime]);

  const handleCellClick = (pincode, hourIndex, count) => {
    if (count <= 0) return;
    setCellModal({
      pincode,
      hour: hourIndex,
      hourLabel: TIME_OPTIONS[hourIndex],
      day: appliedFilters.startDay || evaluationDay,
      status: appliedFilters.status,
    });
  };

  const handleCloseCellModal = () => setCellModal(null);

  const handleExportCsv = () => {
    AvailableDriversService.exportPincodeGridCsv({
      rows,
      evaluationDay,
      filters: appliedFilters,
    });
  };

  const appliedFilterCaption = useMemo(() => {
    const parts = [
      `Day: ${appliedFilters.startDay || evaluationDay || "N/A"}`,
      `Status: ${STATUS_OPTIONS.find((o) => o.value === appliedFilters.status)?.label || "Both"}`,
    ];
    if (appliedFilters.startTime) {
      parts.push(`Time Period: ${appliedFilters.startTime}`);
    }
    if (appliedFilters.pincode?.length === 6) {
      parts.push(`Pincode: ${appliedFilters.pincode}`);
    }
    return parts.join(" · ");
  }, [appliedFilters, evaluationDay]);

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-50 min-h-screen">
      <div className="flex items-center space-x-3 mb-8">
        <FaUserCheck className="text-3xl text-blue-600" />
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Available Drivers</h2>
          <p className="text-sm text-gray-600">
            Onboarded driver counts by pincode and hour
            {evaluationDay ? ` (evaluated for ${evaluationDay})` : ""}.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          Filters
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Day
            </label>
            <select
              className={selectClassName}
              value={formFilters.startDay}
              onChange={(e) => handleFilterChange("startDay", e.target.value)}
            >
              {WEEK_DAYS.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Period
            </label>
            <select
              className={selectClassName}
              value={formFilters.startTime}
              onChange={(e) => handleFilterChange("startTime", e.target.value)}
            >
              <option value="">Select Time</option>
              {TIME_OPTIONS.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pincode
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              className={selectClassName}
              value={formFilters.pincode}
              onChange={(e) => handleFilterChange("pincode", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className={selectClassName}
              value={formFilters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end justify-center gap-3">
            <button
              type="button"
              onClick={handleApplyFilter}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Apply Filter
            </button>
          </div>
          <div>
            <button
              type="button"
              onClick={handleReset}
              className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Driver Summary
            </h3>
            <p className="text-xs text-gray-500 mt-1">{appliedFilterCaption}</p>
          </div>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={loading || rows.length === 0}
            className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <FaDownload />
            Export CSV
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-600">Total Onboarded Drivers</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {loading ? "—" : summary.totalOnboarded}
            </p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-medium text-green-700">Online Drivers</p>
            <p className="text-3xl font-bold text-green-800 mt-2">
              {loading ? "—" : summary.online}
            </p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">Offline Drivers</p>
            <p className="text-3xl font-bold text-red-800 mt-2">
              {loading ? "—" : summary.offline}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
          <table className="w-full min-w-max border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
              <tr>
                <th
                  rowSpan={2}
                  className="sticky left-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200 min-w-[6rem] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                >
                  Pin Code
                </th>
                <th
                  colSpan={TIME_OPTIONS.length}
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50"
                >
                  24 hours Time Period
                </th>
              </tr>
              <tr>
                {TIME_OPTIONS.map((hour, index) => (
                  <th
                    key={hour}
                    className={`px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 min-w-[3rem] whitespace-nowrap bg-gray-50 ${
                      index === highlightedHourIndex ? highlightColumnClass : ""
                    }`}
                  >
                    {hour}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={1 + TIME_OPTIONS.length}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    Loading available drivers...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + TIME_OPTIONS.length}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No pincode rows match the filter.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.pincode} className="group hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      {row.pincode}
                    </td>
                    {row.hourlyCounts.map((count, index) => (
                      <td
                        key={`${row.pincode}-${TIME_OPTIONS[index]}`}
                        className={`px-2 py-3 text-center text-sm text-gray-700 min-w-[3rem] ${
                          index === highlightedHourIndex ? highlightColumnClass : ""
                        } ${count > 0 ? "cursor-pointer hover:text-blue-600 hover:underline font-medium" : ""}`}
                        onClick={() => handleCellClick(row.pincode, index, count)}
                        role={count > 0 ? "button" : undefined}
                        tabIndex={count > 0 ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (count > 0 && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            handleCellClick(row.pincode, index, count);
                          }
                        }}
                      >
                        {count}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AvailableDriversCellModal
        show={Boolean(cellModal)}
        day={cellModal?.day}
        pincode={cellModal?.pincode}
        hour={cellModal?.hour}
        hourLabel={cellModal?.hourLabel}
        status={cellModal?.status}
        onClose={handleCloseCellModal}
      />
    </div>
  );
};

export default AvailableDrivers;
